import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, ScanLine, Ticket, PlusSquare, UserPlus,
  History, FileBarChart, ScrollText, Settings, Users, Vote, Printer, LogOut, Menu, X, UtensilsCrossed, Eye, ClipboardCheck, QrCode,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/dashboard", label: "Dashboard",           icon: LayoutDashboard, roles: ["admin"] },
  { to: "/scan",      label: "Scan Ticket",          icon: ScanLine,        roles: ["admin", "bar"] },
  { to: "/check-in",  label: "Guest Check-In",       icon: ClipboardCheck,  roles: ["admin", "registration"] },
  { to: "/tickets",   label: "Tickets",              icon: Ticket,          roles: ["admin", "registration"] },
  { to: "/generate",  label: "Generate Tickets",     icon: PlusSquare,      roles: ["admin"] },
  { to: "/add",       label: "Add Ticket",           icon: UserPlus,        roles: ["admin"] },
  { to: "/print",     label: "Print Tickets",        icon: Printer,         roles: ["admin"] },
  { to: "/recharge-qr", label: "Guest Portal QR",        icon: QrCode,          roles: ["admin"] },
  { to: "/history",   label: "Redemption History",   icon: History,         roles: ["admin"] },
  { to: "/purchases", label: "Drink Sales",          icon: Ticket,          roles: ["admin", "registration"] },
  { to: "/voting",    label: "Voting",               icon: Vote,            roles: ["admin"] },
  { to: "/reports",   label: "Reports",              icon: FileBarChart,    roles: ["admin"] },
  { to: "/roles",     label: "Roles",                icon: Users,           roles: ["admin"] },
  { to: "/activity",  label: "Activity Log",         icon: ScrollText,      roles: ["admin"] },
  { to: "/settings",  label: "Event Settings",       icon: Settings,        roles: ["admin"] },
];

const ROLE_HOME = { admin: "/dashboard", registration: "/tickets", bar: "/scan" };

function Wordmark() {
  return (
    <Link to="/dashboard" className="flex items-center gap-3 group" data-testid="brand-wordmark">
      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-orange-500 to-rose-500 flex items-center justify-center shadow-lg shadow-orange-500/30">
        <UtensilsCrossed className="text-white" size={22} />
      </div>
      <div className="leading-none">
        <div className="font-display text-2xl font-black text-white tracking-tight">FOOD</div>
        <div className="font-display text-2xl font-black text-orange-400 tracking-tight -mt-1">BAZAAR</div>
      </div>
    </Link>
  );
}

function ViewAsBanner({ viewAs, setViewAs }) {
  if (!viewAs) return null;
  return (
    <div className="sticky top-0 z-30 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 flex items-center justify-between text-sm font-bold" data-testid="view-as-banner">
      <div className="flex items-center gap-2">
        <Eye size={16} /> VIEWING AS <span className="uppercase bg-black/25 px-2 py-0.5 rounded">{viewAs}</span>
        <span className="font-normal opacity-90 hidden sm:inline">— actions still run under your Admin account.</span>
      </div>
      <button onClick={() => setViewAs(null)} className="bg-black/25 hover:bg-black/40 rounded px-3 py-1 text-xs tracking-widest uppercase" data-testid="view-as-exit">Return to Admin</button>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout, viewAs, setViewAs, effectiveRole } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);

  const items = NAV.filter((n) => {
    // Accept both new and legacy role names
    const r = effectiveRole === "reception" ? "registration" : effectiveRole === "tickets" ? "bar" : effectiveRole;
    return n.roles.includes(r);
  });
  const onLogout = async () => { await logout(); nav("/login"); };

  const switchViewAs = (role) => {
    setViewAs(role);
    if (role) nav(ROLE_HOME[role] || "/dashboard");
    else nav("/dashboard");
  };

  const link = "flex items-center gap-3 px-3 py-2.5 rounded-lg text-slate-300 hover:bg-white/5 transition-colors";
  const active = "bg-orange-500/15 text-orange-300 border border-orange-500/30";

  return (
    <div className="min-h-screen bg-[#0B1120] text-slate-100">
      <ViewAsBanner viewAs={viewAs} setViewAs={setViewAs} />

      <div className="md:hidden sticky top-0 z-20 flex items-center justify-between px-4 py-3 bg-[#0B1120]/95 backdrop-blur border-b border-slate-800">
        <Wordmark />
        <button onClick={() => setOpen(!open)} className="p-2 rounded-md bg-slate-800 text-slate-100" data-testid="mobile-menu-toggle">
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      <aside className="hidden md:flex flex-col fixed left-0 top-0 h-full w-64 bg-[#0B1120] border-r border-slate-800 p-4 z-30">
        <div className="mb-8"><Wordmark /></div>
        <nav className="flex-1 space-y-1 overflow-y-auto pr-1">
          {items.map((n) => (
            <NavLink key={n.to} to={n.to} className={({ isActive }) => `${link} ${isActive ? active : ""}`} data-testid={`nav-${n.to.slice(1)}`}>
              <n.icon size={18} />
              <span className="text-sm font-semibold">{n.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="pt-4 mt-auto border-t border-slate-800 space-y-3">
          {user?.role === "admin" && (
            <div data-testid="view-as-switcher">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">View As</div>
              <select
                value={viewAs || "admin"}
                onChange={(e) => switchViewAs(e.target.value === "admin" ? null : e.target.value)}
                className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200"
                data-testid="view-as-select"
              >
                <option value="admin">Admin (you)</option>
                <option value="registration">Registration</option>
                <option value="bar">Bar</option>
              </select>
            </div>
          )}
          <div className="text-xs text-slate-400 truncate" data-testid="current-user-email">
            {user?.email}
            <span className="ml-2 px-2 py-0.5 rounded bg-orange-500/20 text-orange-300 text-[10px] uppercase font-bold tracking-wider">
              {user?.role === "reception" ? "registration" : user?.role === "tickets" ? "bar" : user?.role}
            </span>
          </div>
          <Button onClick={onLogout} variant="outline" className="w-full bg-slate-900 border-slate-700 hover:bg-slate-800 text-slate-200" data-testid="logout-btn">
            <LogOut size={16} className="mr-2" /> Log out
          </Button>
        </div>
      </aside>

      {open && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/70" onClick={() => setOpen(false)}>
          <div className="w-72 h-full bg-[#0B1120] p-4 border-r border-slate-800" onClick={(e) => e.stopPropagation()}>
            <div className="mb-6"><Wordmark /></div>
            {user?.role === "admin" && (
              <div className="mb-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">View As</div>
                <select value={viewAs || "admin"} onChange={(e) => { switchViewAs(e.target.value === "admin" ? null : e.target.value); setOpen(false); }}
                  className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-xs text-slate-200">
                  <option value="admin">Admin (you)</option>
                  <option value="registration">Registration</option>
                  <option value="bar">Bar</option>
                </select>
              </div>
            )}
            <nav className="space-y-1">
              {items.map((n) => (
                <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} className={({ isActive }) => `${link} ${isActive ? active : ""}`}>
                  <n.icon size={18} /><span className="text-sm font-semibold">{n.label}</span>
                </NavLink>
              ))}
            </nav>
            <Button onClick={onLogout} className="mt-6 w-full bg-slate-900 border border-slate-700 text-slate-200" data-testid="logout-btn-mobile">
              <LogOut size={16} className="mr-2" /> Log out
            </Button>
          </div>
        </div>
      )}

      <main className="md:ml-64 p-4 sm:p-6 lg:p-10 min-h-screen">
        {children}
      </main>
    </div>
  );
}
